import {
  Centered,
  EllipsedText,
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  useLoading,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { RecoilState, useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useStyles } from '../../hooks/useStyles';
import { getEvidenceMediaDetail } from '../../services/evidenceService';
import { apiParamsState } from '../../storage/app';
import { evidenceMediaState } from '../../storage/evidences';
import EvidenceMediaDetail from '../evidences/EvidenceMediaDetail';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { FloatingCardsLayout } from '../layout/FloatingCardsLayout';
import { StyledTooltip } from '../ui/StyledTooltip';

type Props = Readonly<{
  redirectRoute: string;
  fromExternalRedirectRoute: string;
  internalState: RecoilState<any>;
}>;

function EvidenceMediaDetailPage({ redirectRoute, fromExternalRedirectRoute, internalState }: Props) {
  const { id: workspaceId, evidenceId, notificationId } = useParams();
  const { theme, css, classes } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const [media, setMedia] = useRecoilState(evidenceMediaState);
  const resetMedia = useResetRecoilState(evidenceMediaState);
  const navigate = useNavigate();
  const apiParams = useRecoilValue(apiParamsState);
  const fromExternal = !useRecoilValue(internalState);

  useEffect(() => {
    return () => {
      resetMedia();
    };
  }, []);

  useEffect(() => {
    const loadEvidenceId = async () => {
      if (!evidenceId || !apiParams) {
        void navigate(generatePath(redirectRoute, { id: workspaceId, notificationId }), { replace: true });
        return;
      }
      startLoading();
      const { data, error } = await getEvidenceMediaDetail(apiParams.clientId, evidenceId, apiParams.subClientId);
      if (data && !error) {
        setMedia(data);
      }
      stopLoading();
    };

    void loadEvidenceId();
  }, [evidenceId, apiParams, workspaceId]);

  const closeDetail = () => {
    if (fromExternal) {
      void navigate(generatePath(fromExternalRedirectRoute, { id: workspaceId, notificationId }));
    } else {
      void navigate(generatePath(redirectRoute, { id: workspaceId, notificationId }));
    }
  };

  return (
    <FloatingCardsLayout
      content={
        <FloatingCardContent
          flexible
          gap={theme.sizing.scale600}
        >
          {loading && !media && <Loading />}
          {!loading && !media && (
            <FlexColumn
              gap={theme.sizing.scale400}
              classNames={css({ flex: 1, overflow: 'hidden' })}
            >
              <FlexRow classNames={classes.centeredBetween}>
                <FlexColumn
                  gap={0}
                  classNames={css({ width: '100%' })}
                >
                  <FlexRow
                    gap={theme.sizing.scale200}
                    classNames={classes.centeredBetween}
                  >
                    <FlexRow
                      gap={theme.sizing.scale200}
                      centered
                    >
                      <ParagraphMedium
                        margin={0}
                        style={{ fontWeight: '700' }}
                      >
                        <EllipsedText
                          inline
                          breakAll
                        >
                          {evidenceId ?? ''}
                        </EllipsedText>
                      </ParagraphMedium>
                    </FlexRow>
                    <StyledTooltip
                      content="Cerrar detalle"
                      showArrow={false}
                    >
                      <Button
                        shape="circle"
                        kind="tertiary"
                        size="compact"
                        onClick={closeDetail}
                      >
                        <MaterialIcon
                          name="close"
                          size="mini"
                        />
                      </Button>
                    </StyledTooltip>
                  </FlexRow>
                </FlexColumn>
              </FlexRow>
              <Centered>
                <EmptyState title="Evidencia no encontrada" />
              </Centered>
            </FlexColumn>
          )}
          {media && <EvidenceMediaDetail onClose={closeDetail} />}
        </FloatingCardContent>
      }
      contentSize={550}
    />
  );
}

export default EvidenceMediaDetailPage;
